import { useCallback, useEffect, useMemo, useState } from 'react'
import jobSeekerApi, {
    JobSeekerSubmissionSummary,
} from '../../api/jobSeeker/JobSeekerApi'
import PersonSmallPic from '../../components/PersonSmallPic'
import { PersonDto } from '../../api/person/models/PersonDto'
import personApi from '../../api/person/PersonApi'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faRefresh } from '@fortawesome/free-solid-svg-icons'
import { formatPersonName } from '@/utils/personUtils'
import Button from '../../uiV2/common/form/inputs/Button'

type JobSeekersTabProps = {
    isActive: boolean
}

const PAGE_SIZE = 100
const EXPORT_PAGE_SIZE = 500
const PERSON_QUERY_CHUNK_SIZE = 100
const JOB_SEEKER_TABLE_CLASS =
    'main-table w-full min-w-[1000px] text-[11px] leading-tight'
const JOB_SEEKER_HEADER_CELL_CLASS =
    'px-2 py-1 text-left align-top whitespace-normal'
const JOB_SEEKER_CELL_CLASS = 'px-2 py-1 align-top'

function JobSeekersTab({ isActive }: JobSeekersTabProps): React.JSX.Element {
    const [jobSeekers, setJobSeekers] = useState<JobSeekerSubmissionSummary[]>(
        [],
    )
    const [personsById, setPersonsById] = useState<Map<string, PersonDto>>(
        () => new Map(),
    )
    const [isLoading, setIsLoading] = useState(false)
    const [isExporting, setIsExporting] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [hasRequested, setHasRequested] = useState(false)
    const [currentPage, setCurrentPage] = useState(1)
    const [totalCount, setTotalCount] = useState(0)

    const fetchJobSeekers = useCallback(async (page: number) => {
        setHasRequested(true)
        setIsLoading(true)
        setError(null)
        try {
            const res = await jobSeekerApi.getSubmissions({
                page,
                pageSize: PAGE_SIZE,
            })
            setTotalCount(res.total)
            const maxPage = Math.max(1, Math.ceil(res.total / PAGE_SIZE))
            if (page > maxPage && res.total > 0) {
                setCurrentPage(maxPage)
            }
            setJobSeekers(res.submissions)

            const personIds = Array.from(
                new Set(
                    res.submissions
                        .map((submission) => submission.linkedPersonId)
                        .filter((id): id is string => Boolean(id)),
                ),
            )

            if (personIds.length > 0) {
                try {
                    const personsRes = await personApi.getPersonsById(personIds)
                    setPersonsById(
                        new Map(
                            personsRes.persons.map((person) => [
                                person.id,
                                person,
                            ]),
                        ),
                    )
                } catch (personError) {
                    console.error(
                        'Failed to fetch linked person data',
                        personError,
                    )
                    setPersonsById(new Map())
                }
            } else {
                setPersonsById(new Map())
            }
        } catch (err) {
            console.error('Failed to fetch job seeker submissions', err)
            setError(
                err instanceof Error
                    ? err.message
                    : 'Failed to load job seeker submissions',
            )
        } finally {
            setIsLoading(false)
        }
    }, [])

    useEffect(() => {
        if (isActive) {
            void fetchJobSeekers(currentPage)
        }
    }, [fetchJobSeekers, isActive, currentPage])

    const hasResults = totalCount > 0
    const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))
    const handleExportCsv = useCallback(async () => {
        if (!hasResults || isExporting) {
            return
        }

        setIsExporting(true)
        setError(null)

        const headers = [
            'User Id',
            'Name',
            'Email',
            'LinkedIn URL',
            'Phone',
            'Linked Person Name',
            'Source',
            'Job',
            'Accepted Terms',
            'Signed Up At',
        ]

        const escapeValue = (value: string | null | undefined): string => {
            if (value == null) {
                return ''
            }
            const str = String(value)
            if (str.includes('"') || str.includes(',') || str.includes('\n')) {
                return `"${str.replace(/"/g, '""')}"`
            }
            return str
        }

        const getLinkedPersonIds = (
            submissions: JobSeekerSubmissionSummary[],
        ) =>
            Array.from(
                new Set(
                    submissions
                        .map((submission) => submission.linkedPersonId)
                        .filter((id): id is string => Boolean(id)),
                ),
            )

        const loadPersonsByIds = async (
            personIds: string[],
            intoMap: Map<string, PersonDto>,
        ) => {
            if (personIds.length === 0) {
                return
            }

            const personIdChunks: string[][] = []
            for (
                let i = 0;
                i < personIds.length;
                i += PERSON_QUERY_CHUNK_SIZE
            ) {
                personIdChunks.push(
                    personIds.slice(i, i + PERSON_QUERY_CHUNK_SIZE),
                )
            }

            const personResponses = await Promise.all(
                personIdChunks.map(async (personIdsChunk) =>
                    personApi.getPersonsById(personIdsChunk),
                ),
            )

            personResponses.forEach((personResponse) => {
                personResponse.persons.forEach((person) => {
                    intoMap.set(person.id, person)
                })
            })
        }

        try {
            const allSubmissions: JobSeekerSubmissionSummary[] = []
            const exportPersonsById = new Map<string, PersonDto>()

            const firstPage = await jobSeekerApi.getSubmissions({
                page: 1,
                pageSize: EXPORT_PAGE_SIZE,
            })

            allSubmissions.push(...firstPage.submissions)
            await loadPersonsByIds(
                getLinkedPersonIds(firstPage.submissions),
                exportPersonsById,
            )

            const exportTotalPages = Math.max(
                1,
                Math.ceil(firstPage.total / EXPORT_PAGE_SIZE),
            )
            for (let page = 2; page <= exportTotalPages; page += 1) {
                const pageRes = await jobSeekerApi.getSubmissions({
                    page,
                    pageSize: EXPORT_PAGE_SIZE,
                })

                allSubmissions.push(...pageRes.submissions)
                await loadPersonsByIds(
                    getLinkedPersonIds(pageRes.submissions),
                    exportPersonsById,
                )
            }

            const rows = allSubmissions.map((entry) => {
                const person =
                    entry.linkedPersonId &&
                    exportPersonsById.get(entry.linkedPersonId)
                const linkedPersonName = person ? formatPersonName(person) : ''

                return [
                    entry.userId,
                    entry.name,
                    entry.email,
                    entry.linkedinUrl ?? '',
                    entry.phone ?? '',
                    linkedPersonName,
                    entry.source ?? '',
                    entry.screeningJob
                        ? [
                              entry.screeningJob.jobTitle,
                              entry.screeningJob.clientName
                                  ? `Client: ${entry.screeningJob.clientName}`
                                  : '',
                              `Job ID: ${entry.screeningJob.id}`,
                          ]
                              .filter(Boolean)
                              .join(' | ')
                        : '',
                    entry.acceptedTerms ? 'Yes' : 'No',
                    entry.userCreatedAt,
                ]
            })

            const csvContent = [
                headers.map(escapeValue).join(','),
                ...rows.map((row) => row.map(escapeValue).join(',')),
            ].join('\n')

            const blob = new Blob([csvContent], {
                type: 'text/csv;charset=utf-8;',
            })
            const url = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.setAttribute(
                'download',
                `job-seekers-${new Date().toISOString()}.csv`,
            )
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            URL.revokeObjectURL(url)
        } catch (err) {
            console.error('Failed to export job seeker submissions', err)
            setError(
                err instanceof Error
                    ? err.message
                    : 'Failed to export job seeker submissions',
            )
        } finally {
            setIsExporting(false)
        }
    }, [hasResults, isExporting])

    const tableRows = useMemo(
        () =>
            jobSeekers.map((entry) => {
                const linkedPersonId = entry.linkedPersonId
                const person = linkedPersonId
                    ? personsById.get(linkedPersonId)
                    : undefined
                return (
                    <tr key={entry.submissionId}>
                        <td className={JOB_SEEKER_CELL_CLASS}>{entry.name}</td>
                        <td className={JOB_SEEKER_CELL_CLASS}>
                            <div className="flex flex-col gap-1">
                                <span>{entry.email}</span>
                                <div className="text-[10px] text-gray-500">
                                    <div>User ID: {entry.userId}</div>
                                    <div>
                                        User Name:{' '}
                                        {entry.userName?.trim() || '—'}
                                    </div>
                                    <div>
                                        User Email:{' '}
                                        {entry.userEmail?.trim() || '—'}
                                    </div>
                                </div>
                            </div>
                        </td>
                        <td className={JOB_SEEKER_CELL_CLASS}>
                            {entry.linkedinUrl ? (
                                <a
                                    className="text-blue-600 hover:underline text-xs font-semibold break-all"
                                    href={entry.linkedinUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    {entry.linkedinUrl}
                                </a>
                            ) : (
                                <span className="text-gray-500 text-xs">—</span>
                            )}
                        </td>
                        <td className={JOB_SEEKER_CELL_CLASS}>
                            {entry.phone ?? '—'}
                        </td>
                        <td className={JOB_SEEKER_CELL_CLASS}>
                            {person ? (
                                <div className="flex items-center gap-3">
                                    <PersonSmallPic
                                        person={person}
                                        size="28px"
                                    />
                                    <div className="flex flex-col">
                                        <span className="text-[11px] font-semibold leading-tight">
                                            {formatPersonName(person)}
                                        </span>
                                    </div>
                                </div>
                            ) : (
                                <span className="text-gray-500 text-xs">
                                    {entry.linkedPersonId
                                        ? `Person ID: ${entry.linkedPersonId}`
                                        : 'Not linked'}
                                </span>
                            )}
                        </td>
                        <td className={JOB_SEEKER_CELL_CLASS}>
                            {entry.source ?? '—'}
                        </td>
                        <td className={JOB_SEEKER_CELL_CLASS}>
                            {entry.screeningJob ? (
                                <div className="flex flex-col gap-1 text-[10px] text-gray-700">
                                    <span className="text-[11px] font-semibold leading-tight">
                                        {entry.screeningJob.jobTitle}
                                    </span>
                                    {entry.screeningJob.clientName ? (
                                        <span>
                                            Client:{' '}
                                            {entry.screeningJob.clientName}
                                        </span>
                                    ) : null}
                                    <span>ID: {entry.screeningJob.id}</span>
                                </div>
                            ) : (
                                <span className="text-gray-500 text-xs">—</span>
                            )}
                        </td>
                        <td className={JOB_SEEKER_CELL_CLASS}>
                            {entry.acceptedTerms ? 'Yes' : 'No'}
                        </td>
                        <td className={JOB_SEEKER_CELL_CLASS}>
                            {entry.userCreatedAt}
                        </td>
                    </tr>
                )
            }),
        [jobSeekers, personsById],
    )

    return (
        <div>
            <div className="flex items-center justify-between">
                <h3 className="page-title">Job Seekers</h3>
                <div className="flex gap-2">
                    <button
                        className="rounded-full bg-[#153bab] w-12 h-12 flex items-center justify-center text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                        onClick={() => {
                            void fetchJobSeekers(currentPage)
                        }}
                        disabled={isLoading || isExporting}
                        title="Refresh"
                    >
                        <FontAwesomeIcon icon={faRefresh} />
                    </button>
                </div>
            </div>
            <button
                className="rounded bg-white px-4 py-2 text-sm font-semibold text-[#153bab] border border-[#153bab] shadow-sm transition hover:bg-[#153bab] hover:text-white disabled:cursor-not-allowed disabled:opacity-60 mt-4"
                onClick={() => {
                    void handleExportCsv()
                }}
                disabled={!hasResults || isLoading || isExporting}
            >
                {isExporting ? 'Exporting...' : 'Export CSV'}
            </button>
            {error && (
                <div className="mt-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    {error}
                </div>
            )}
            {isLoading && !hasResults ? (
                <p className="mt-4 text-sm text-gray-500">
                    Loading job seeker submissions…
                </p>
            ) : !hasResults ? (
                <p className="mt-4 text-sm text-gray-500">
                    {hasRequested
                        ? 'No job seeker signups found yet.'
                        : 'Select this tab to load job seeker signups.'}
                </p>
            ) : (
                <>
                    <div className="overflow-x-auto">
                        <table className={JOB_SEEKER_TABLE_CLASS}>
                            <thead>
                                <tr className="text-[10px] uppercase tracking-wide">
                                    <th
                                        className={JOB_SEEKER_HEADER_CELL_CLASS}
                                    >
                                        Name
                                    </th>
                                    <th
                                        className={JOB_SEEKER_HEADER_CELL_CLASS}
                                    >
                                        Email
                                    </th>
                                    <th
                                        className={JOB_SEEKER_HEADER_CELL_CLASS}
                                    >
                                        LinkedIn
                                    </th>
                                    <th
                                        className={JOB_SEEKER_HEADER_CELL_CLASS}
                                    >
                                        Phone
                                    </th>
                                    <th
                                        className={JOB_SEEKER_HEADER_CELL_CLASS}
                                    >
                                        Person
                                    </th>
                                    <th
                                        className={JOB_SEEKER_HEADER_CELL_CLASS}
                                    >
                                        Source
                                    </th>
                                    <th
                                        className={JOB_SEEKER_HEADER_CELL_CLASS}
                                    >
                                        Job
                                    </th>
                                    <th
                                        className={JOB_SEEKER_HEADER_CELL_CLASS}
                                    >
                                        Agreed to Ts&Cs
                                    </th>
                                    <th
                                        className={JOB_SEEKER_HEADER_CELL_CLASS}
                                    >
                                        Signed Up At (UTC)
                                    </th>
                                </tr>
                            </thead>
                            <tbody>{tableRows}</tbody>
                        </table>
                    </div>
                    <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
                        <Button
                            type="button"
                            style="secondary"
                            onClick={() =>
                                setCurrentPage((page) => Math.max(1, page - 1))
                            }
                            enabled={
                                currentPage > 1 && !isLoading && !isExporting
                            }
                        >
                            Previous
                        </Button>
                        <div>
                            Page {currentPage} · Showing {jobSeekers.length} ·
                            Per page {PAGE_SIZE}
                        </div>
                        <Button
                            type="button"
                            style="secondary"
                            onClick={() =>
                                setCurrentPage((page) =>
                                    Math.min(totalPages, page + 1),
                                )
                            }
                            enabled={
                                !isLoading &&
                                !isExporting &&
                                currentPage < totalPages &&
                                jobSeekers.length > 0
                            }
                        >
                            Next
                        </Button>
                    </div>
                </>
            )}
        </div>
    )
}

export default JobSeekersTab
